const chapter = {
  id: 'organisms-populations',
  slug: 'organisms-populations',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Organisms and Populations',
        hindi: 'जीव और समष्टि',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '2-3 questions in NEET',
        hindi: 'NEET में 2-3 प्रश्न',
  },
  syllabusTags: ['Ecology', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'levels',
      title: {
        english: 'Levels of Ecological Organisation',
        hindi: 'पारिस्थितिक संगठन के स्तर',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Hierarchy: organism → **population** → **community** → **ecosystem** → **biome** → **biosphere**.',
        hindi: 'पदानुक्रम: जीव → **जनसंख्या** → **समुदाय** → **पारिस्थितिकी तंत्र** → **बायोम** → **जीवमंडल**।',
            },
            {
              english: '**Ecology** = study of interactions of organisms with each other and with the environment.',
        hindi: '**पारिस्थितिकी** = जीवों की एक दूसरे के साथ और पर्यावरण के साथ बातचीत का अध्ययन।',
            },
            {
              english: 'Major **biomes of India**: tropical rain forest, deciduous forest, desert, grassland, alpine.',
        hindi: 'प्रमुख **भारत के बायोम**: उष्णकटिबंधीय वर्षा वन, पर्णपाती वन, रेगिस्तान, घास के मैदान, अल्पाइन।',
            },
          ],
        },
      ],
    },
    {
      id: 'abiotic-factors',
      title: {
        english: 'Major Abiotic Factors',
        hindi: 'प्रमुख अजैव कारक',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Temperature** — most ecologically relevant. Controls enzyme kinetics & basal metabolism.',
        hindi: '**तापमान** — पारिस्थितिक रूप से सर्वाधिक प्रासंगिक। एंजाइम कैनेटीक्स और बेसल चयापचय को नियंत्रित करता है।',
            },
            {
              english: '• **Eurythermal** = wide tolerance (humans). **Stenothermal** = narrow tolerance.',
        hindi: '• **यूरीथर्मल** = व्यापक सहनशीलता (मानव)। **स्टेनोथर्मल** = संकीर्ण सहनशीलता।',
            },
            {
              english: '**Water** — second most important (especially on land).',
        hindi: '**जल** - दूसरा सबसे महत्वपूर्ण (विशेषकर भूमि पर)।',
            },
            {
              english: '• **Euryhaline / stenohaline** for salinity tolerance.',
        hindi: '• **लवणता सहनशीलता के लिए यूरीहैलाइन/स्टेनोहैलाइन**।',
            },
            {
              english: '**Light** — drives photosynthesis; affects flowering (photoperiodism), foraging, migration.',
        hindi: '**प्रकाश** - प्रकाश संश्लेषण को संचालित करता है; पुष्पन (फोटोपेरियोडिज्म), चारागाह, प्रवासन को प्रभावित करता है।',
            },
            {
              english: '**Soil** — texture, pH, mineral composition; determines plant distribution → animal distribution.',
        hindi: '**मिट्टी** - बनावट, पीएच, खनिज संरचना; पौधों का वितरण → पशु वितरण निर्धारित करता है।',
            },
            {
              english: 'All organisms maintain **homeostasis** (constant internal environment).',
        hindi: 'सभी जीव **होमियोस्टैसिस** (निरंतर आंतरिक वातावरण) बनाए रखते हैं।',
            },
          ],
        },
      ],
    },
    {
      id: 'responses',
      title: {
        english: 'Responses to Abiotic Factors',
        hindi: 'अजैव कारकों के प्रति प्रतिक्रियाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Regulate** — maintain constant internal milieu by physiological/behavioural means. All birds, mammals, very few lower vertebrates/invertebrates. Thermoregulation + osmoregulation. **Costly** (energetically).',
        hindi: '**विनियमित** - physiological/behavioural साधनों द्वारा निरंतर आंतरिक वातावरण बनाए रखें। सभी पक्षी, स्तनधारी, बहुत कम निचले vertebrates/invertebrates। थर्मोरेग्यूलेशन + ऑस्मोरग्यूलेशन। **महंगा** (ऊर्जावान रूप से)।',
            },
            {
              english: '**Conform** — internal conditions follow ambient. ~99 % of animals & nearly all plants. Cheap but limited to narrow environment.',
        hindi: '**अनुरूप** - आंतरिक स्थितियाँ परिवेश का अनुसरण करती हैं। ~99% जानवर और लगभग सभी पौधे। सस्ता लेकिन संकीर्ण वातावरण तक सीमित।',
            },
            {
              english: '**Migrate** — temporary movement from stressful to favourable habitat (e.g. Siberian birds → Bharatpur in winter).',
        hindi: '**प्रवास** - तनावपूर्ण से अनुकूल निवास स्थान की ओर अस्थायी स्थानांतरण (जैसे साइबेरियाई पक्षी → सर्दियों में भरतपुर)।',
            },
            {
              english: '**Suspend** — reduced metabolism:',
        hindi: '**निलंबित** — चयापचय में कमी:',
            },
            {
              english: '• **Hibernation** — winter sleep (bears, frogs).',
        hindi: '• **हाइबरनेशन** - शीतकालीन नींद (भालू, मेंढक)।',
            },
            {
              english: '• **Aestivation** — summer sleep (snails, fishes in dry mud).',
        hindi: '• **एस्टीवेशन** - ग्रीष्मकालीन नींद (घोंघे, सूखी मिट्टी में मछलियाँ)।',
            },
            {
              english: '• **Diapause** — suspended development in zooplankton in unfavourable conditions.',
        hindi: '• **डायपॉज** - प्रतिकूल परिस्थितियों में ज़ोप्लांकटन में निलंबित विकास।',
            },
            {
              english: '• **Spores** — bacteria, fungi, lower plants.',
        hindi: '• **बीजाणु** - बैक्टीरिया, कवक, निचले पौधे।',
            },
          ],
        },
      ],
    },
    {
      id: 'adaptations',
      title: {
        english: 'Adaptations',
        hindi: 'अनुकूलन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Adaptation = morphological/physiological/behavioural trait enabling survival & reproduction.',
        hindi: 'अनुकूलन = जीवित रहने और प्रजनन को सक्षम करने वाली रूपात्मक/शारीरिक/व्यवहारात्मक विशेषता।',
            },
            {
              english: '**Kangaroo rat (deserts)** — meets all water needs from internal fat oxidation; concentrated urine.',
        hindi: '**कंगारू चूहा (रेगिस्तान)** - आंतरिक वसा ऑक्सीकरण से पानी की सभी जरूरतों को पूरा करता है; गाढ़ा मूत्र.',
            },
            {
              english: '**Xerophytes** — thick cuticle, sunken stomata, modified leaves (spines), CAM photosynthesis (Opuntia).',
        hindi: '**ज़ीरोफाइट्स** - मोटी छल्ली, धँसा हुआ रंध्र, संशोधित पत्तियाँ (रीढ़ें), सीएएम प्रकाश संश्लेषण (ओपंटिया)।',
            },
            {
              english: '**High altitude (acclimatisation)** — short term: ↑ RBC production, ↑ breathing rate, ↑ Hb affinity.',
        hindi: '**उच्च ऊंचाई (अनुकूलन)** - अल्पावधि: ↑ आरबीसी उत्पादन, ↑ श्वास दर, ↑ एचबी आत्मीयता।',
            },
            {
              english: '**Allen\'s rule** — mammals from cold regions have shorter ears, limbs (less heat loss).',
        hindi: '**एलन का नियम** - ठंडे क्षेत्रों के स्तनधारियों के कान, अंग छोटे होते हैं (गर्मी का नुकसान कम होता है)।',
            },
            {
              english: '**Bergmann\'s rule** — larger body size in colder climates.',
        hindi: '**बर्गमैन का नियम** - ठंडी जलवायु में शरीर का बड़ा आकार।',
            },
            {
              english: '**Deep-sea fish** — swim bladder absent; specialised enzymes for high pressure.',
        hindi: '**गहरे समुद्र की मछली** - तैरने वाला मूत्राशय अनुपस्थित; उच्च दबाव के लिए विशेष एंजाइम।',
            },
          ],
        },
      ],
    },
    {
      id: 'population-attributes',
      title: {
        english: 'Population Attributes',
        hindi: 'समष्टि विशेषताएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Population** = group of individuals of same species in a given area at a given time.',
        hindi: '**जनसंख्या** = किसी निश्चित समय में किसी क्षेत्र में एक ही प्रजाति के व्यक्तियों का समूह।',
            },
            {
              english: 'Attributes (population, not individual):',
        hindi: 'गुण (जनसंख्या, व्यक्तिगत नहीं):',
            },
            {
              english: '• **Birth rate (natality)** — births per individual per unit time.',
        hindi: '• **जन्म दर (जन्म)** — प्रति व्यक्ति प्रति इकाई समय में जन्म।',
            },
            {
              english: '• **Death rate (mortality)** — deaths per individual per unit time.',
        hindi: '• **मृत्यु दर (मृत्यु दर)** - प्रति इकाई समय में प्रति व्यक्ति मृत्यु।',
            },
            {
              english: '• **Sex ratio** — proportion of males to females.',
        hindi: '• **लिंगानुपात** — पुरुषों से महिलाओं का अनुपात।',
            },
            {
              english: '• **Age distribution** — pre-reproductive, reproductive, post-reproductive. Plotted as **age pyramid** (expanding/stable/declining).',
        hindi: '• **आयु वितरण** - प्रजनन-पूर्व, प्रजनन, प्रजनन-पश्चात। **आयु पिरामिड** (expanding/stable/घटते हुए) के रूप में प्लॉट किया गया।',
            },
            {
              english: '• **Population density (N)** — individuals per unit area. Measured by absolute count, mark-recapture, percent cover, biomass, indicators (pug marks, faecal pellets).',
        hindi: '• **जनसंख्या घनत्व (एन)** - प्रति इकाई क्षेत्र में व्यक्ति। पूर्ण गणना, मार्क-रीकैप्चर, प्रतिशत कवर, बायोमास, संकेतक (पग मार्क, मल छर्रों) द्वारा मापा जाता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'growth-models',
      title: {
        english: 'Population Growth Models',
        hindi: 'समष्टि वृद्धि मॉडल',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: '**dN/dt = (b - d) N = r N**, where b = birth rate, d = death rate, r = **intrinsic rate of natural increase**.',
        hindi: '**डीएन/डीटी = (बी - डी) एन = आर एन**, जहां बी = जन्म दर, डी = मृत्यु दर, आर = **प्राकृतिक वृद्धि की आंतरिक दर**।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Exponential growth** (unlimited resources): $N_t = N_0 e^{rt}$ — **J-shaped curve**.',
        hindi: '**घातीय वृद्धि** (असीमित संसाधन): $N_t = N_0 e^{rt}$ - **J-आकार का वक्र**।',
            },
            {
              english: '**Logistic growth** (limited resources, carrying capacity K):',
        hindi: '**लॉजिस्टिक ग्रोथ** (सीमित संसाधन, वहन क्षमता K):',
            },
          ],
        },
        {
          type: 'equation',
          latex: '\\dfrac{dN}{dt} = rN\\left(\\dfrac{K-N}{K}\\right)',
          label: {
            english: 'Verhulst-Pearl logistic equation; gives **S-shaped (sigmoid) curve**',
        hindi: 'वेरहल्स्ट-पर्ल लॉजिस्टिक समीकरण; **एस-आकार (सिग्मॉइड) वक्र** देता है',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'r values: rat = 0.015 / day; mouse = 0.045; humans = ~0.0205 / yr (India 1981 data).',
        hindi: 'आर मान: चूहा = 0.015 / दिन; माउस = 0.045; मनुष्य = ~0.0205/वर्ष (भारत 1981 डेटा)।',
            },
            {
              english: 'Logistic is more realistic — competition limits growth at K.',
        hindi: 'लॉजिस्टिक अधिक यथार्थवादी है - प्रतिस्पर्धा K पर विकास को सीमित करती है।',
            },
          ],
        },
      ],
    },
    {
      id: 'interactions',
      title: {
        english: 'Population Interactions',
        hindi: 'समष्टि अन्योन्यक्रिया',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Interaction', 'Species A', 'Species B', 'Example'],
          rows: [
            ['Mutualism', '+', '+', 'Lichen (alga + fungus); mycorrhiza; fig–wasp'],
            ['Competition', '–', '–', 'Two birds for same food'],
            ['Predation', '+', '–', 'Lion–zebra'],
            ['Parasitism', '+', '–', 'Cuckoo–crow (brood parasitism); tapeworm in human'],
            ['Commensalism', '+', '0', 'Orchid on mango tree; cattle egret with cattle'],
            ['Amensalism', '–', '0', 'Penicillium killing bacteria'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Gause\'s Competitive Exclusion Principle** — two species competing for same resource cannot coexist indefinitely; better adapted eliminates the other.',
        hindi: '**गॉज़ का प्रतिस्पर्धी बहिष्करण सिद्धांत** - एक ही संसाधन के लिए प्रतिस्पर्धा करने वाली दो प्रजातियाँ अनिश्चित काल तक सह-अस्तित्व में नहीं रह सकतीं; बेहतर ढंग से अनुकूलित दूसरे को ख़त्म कर देता है।',
            },
            {
              english: '**Mimicry** — Monarch butterfly (toxic) mimicked by Viceroy.',
        hindi: '**मिमिक्री** — मोनार्क तितली (विषाक्त) की नकल वायसराय ने की।',
            },
            {
              english: '**Camouflage** — frogs, insects.',
        hindi: '**छलावरण** - मेंढक, कीड़े। **पौधों में सुरक्षा**- कांटे (बबूल), रसायन (निकोटीन, अफ़ीम, कैफीन, कुनैन)।',
            },
            {
              english: '**Defence in plants** — thorns (Acacia), chemicals (nicotine, opium, caffeine, quinine).',
        hindi: '**पौधों में सुरक्षा** - कांटे (बबूल), रसायन (निकोटीन, अफ़ीम, कैफीन, कुनैन)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\dfrac{dN}{dt} = rN \\quad (\\text{exponential, J-shape})',
      desc: {
        english: '',
        hindi: 'डीटी = आरएन (घातीय, जे-आकार)',
      },
    },
    {
      latex: '\\dfrac{dN}{dt} = rN\\left(\\dfrac{K-N}{K}\\right) \\quad (\\text{logistic, S-shape})',
      desc: {
        english: '',
        hindi: 'डीटी = आरएन (के) (लॉजिस्टिक, एस-आकार)',
      },
    },
    {
      latex: 'r = b - d',
      desc: {
        english: 'Intrinsic rate of natural increase',
        hindi: 'प्राकृतिक वृद्धि की आंतरिक दर',
      },
    },
    {
      latex: '\\text{Mutualism (+,+); Predation (+,-); Commensalism (+,0)}',
      desc: {
        english: '',
        hindi: 'पारस्परिकता (+,+); परभक्षण (+,-); सहभोजवाद (+,0)',
      },
    },
    {
      latex: '\\text{Allen: small extremities in cold; Bergmann: large size in cold}',
      desc: {
        english: '',
        hindi: 'एलन: ठंड में छोटे हाथ पैर; बर्गमैन: ठंड में बड़े आकार',
      },
    },
    {
      latex: '\\text{Hibernation (winter); Aestivation (summer)}',
      desc: {
        english: '',
        hindi: 'हाइबरनेशन (सर्दी); सौंदर्यीकरण (ग्रीष्म)',
      },
    },
    {
      latex: '\\text{Eurythermal (wide T); Stenothermal (narrow T)}',
      desc: {
        english: '',
        hindi: 'यूरीथर्मल (चौड़ा टी); स्टेनोथर्मल (संकीर्ण टी)',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Growth',
      problem: {
        english: 'Logistic growth equation is:',
        hindi: 'लॉजिस्टिक विकास समीकरण है:',
      },
      answer: {
        english: 'dN/dt = rN(K−N)/K',
        hindi: 'dN/',
      },
      solution: {
        english: 'Verhulst-Pearl; gives sigmoid curve.',
        hindi: 'वरहल्स्ट-पर्ल; सिग्मॉइड वक्र देता है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Interaction',
      problem: {
        english: 'Lichen represents which interaction?',
        hindi: 'लाइकेन किस अंतःक्रिया का प्रतिनिधित्व करता है?',
      },
      answer: {
        english: 'Mutualism (+, +)',
        hindi: 'पारस्परिकता (+, +)',
      },
      solution: {
        english: 'Algae provide food; fungi provide shelter.',
        hindi: 'शैवाल भोजन प्रदान करते हैं; कवक आश्रय प्रदान करते हैं।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Adaptation',
      problem: {
        english: 'Allen\'s rule states that mammals from cold regions have:',
        hindi: 'एलन का नियम कहता है कि ठंडे क्षेत्रों के स्तनधारियों में:',
      },
      answer: {
        english: 'Shorter ears and limbs',
        hindi: 'छोटे कान और अंग',
      },
      solution: {
        english: 'Reduces heat loss surface area.',
        hindi: 'ताप हानि सतह क्षेत्र को कम करता है।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Suspension',
      problem: {
        english: 'Summer sleep in animals is called:',
        hindi: 'जानवरों में ग्रीष्मकालीन नींद को कहा जाता है:',
      },
      answer: {
        english: 'Aestivation',
        hindi: 'सौंदर्यीकरण',
      },
      solution: {
        english: 'Hibernation = winter sleep.',
        hindi: 'शीतनिद्रा = शीत निद्रा।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Principle',
      problem: {
        english: 'Competitive exclusion principle is given by:',
        hindi: 'प्रतिस्पर्धी बहिष्करण सिद्धांत द्वारा दिया गया है:',
      },
      answer: {
        english: 'G.F. Gause',
        hindi: 'जी.एफ. गौस',
      },
      solution: {
        english: 'Two species with identical niches cannot coexist.',
        hindi: 'समान आधार वाली दो प्रजातियाँ एक साथ नहीं रह सकतीं।',
      },
    },
  ],
  next: {
    slug: 'ecosystem',
    title: 'Ecosystem',
  },
};

export default chapter;
